// Shared browser-side preparation. Originals are never sent to the API.
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const TARGET_BYTES = MAX_UPLOAD_BYTES - 64 * 1024;
let pending = Promise.resolve();

function photoError(message) {
  const error = new Error(message);
  error.response = { data: { msg: message } };
  return error;
}

async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch (_) {
      // Some browsers decode more formats via an image element.
    }
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    return {
      image, width: image.naturalWidth, height: image.naturalHeight,
      close: () => { image.src = ''; URL.revokeObjectURL(url); }
    };
  } catch (error) {
    image.src = '';
    URL.revokeObjectURL(url);
    throw error;
  }
}

async function prepare(file, { maxDimension = 2560 } = {}) {
  let source;
  let canvas;
  try {
    source = await decode(file);
    if (!source.width || !source.height) throw new Error('Invalid dimensions');
    const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
    let width = Math.max(1, Math.round(source.width * scale));
    let height = Math.max(1, Math.round(source.height * scale));
    canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    while (true) {
      canvas.width = width;
      canvas.height = height;
      context.drawImage(source.image, 0, 0, width, height);
      for (const quality of [0.86, 0.72, 0.58, 0.44]) {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
        // Browsers without WebP encoding fall back to PNG, preserving transparency.
        if (blob && blob.size > 0 && blob.size <= TARGET_BYTES &&
            ['image/webp', 'image/png', 'image/jpeg'].includes(blob.type)) {
          const extension = { 'image/webp': 'webp', 'image/png': 'png', 'image/jpeg': 'jpg' }[blob.type];
          const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
          return new File([blob], `${base}.${extension}`, { type: blob.type, lastModified: Date.now() });
        }
      }
      if (width === 1 && height === 1) throw new Error('Image could not be encoded');
      width = Math.max(1, Math.floor(width * 0.75));
      height = Math.max(1, Math.floor(height * 0.75));
    }
  } catch (_) {
    throw photoError('This photo could not be resized in your browser. Try a JPEG, PNG, WEBP, or GIF image, or export the photo as JPEG.');
  } finally {
    if (source) source.close();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

function preparePhoto(file, options) {
  // Serialize decodes across upload widgets, including after a failed conversion.
  const result = pending.then(() => prepare(file, options));
  pending = result.catch(() => {});
  return result;
}

module.exports = { preparePhoto, MAX_UPLOAD_BYTES };
